// ============================================================================
// Piezas compartidas del panel: logo, avatar, botones, chips, estados vacíos y
// esqueletos. Mismo vocabulario visual que victorix-erp-web (familia Victorix).
// Nada de emojis como iconos: SVG (lucide) o vectorial propio.
// ============================================================================
import { useId, type ReactNode } from "react";
import { iniciales } from "./tipos.ts";

// ── Logotipo VX AI (lockup horizontal) ───────────────────────────────────────
// Mismo trazado que el ERP (Geist: VX 800, AI 500). viewBox 0 0 2993 950.
const VX_D = "M274.60003662109375 710.0 18.4000244140625 0.0H197.19873046875L375.599365234375 513.6015625L553.7999877929688 0.0H732.5986938476562L474.7987060546875 710.0Z M747.0000610351562 710.0 991.1998901367188 354.0 749.0000610351562 0.0H950.1985473632812L1095.1991577148438 223.6009521484375L1234.9998168945312 0.0H1437.3982543945312L1195.9984130859375 352.0L1439.7982788085938 710.0H1238.5997924804688L1093.1991577148438 482.3990478515625L949.3984985351562 710.0Z";
const AI_D = "M1818.5999755859375 710.0 2074.5999755859375 0.0H2212.0012817382812L2468.0012817382812 710.0H2352.7999877929688L2288.0003662109375 525.0010375976562H1998.40087890625L1933.6012573242188 710.0ZM2032.0011596679688 427.40020751953125H2254.60009765625L2143.400634765625 103.20172119140625Z M2558.9996337890625 710.0V0.0H2667.0009765625V710.0Z";

export function LogoVXAI({
  className = "", variant = "auto", glow = false,
}: { className?: string; variant?: "auto" | "onDark"; glow?: boolean }) {
  const id = useId();
  const onDark = variant === "onDark";
  return (
    <svg viewBox="0 0 2993 950" fill="none" role="img" aria-label="VX AI" className={className}>
      {glow && (
        <defs>
          <filter id={id} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="35" />
          </filter>
        </defs>
      )}
      <g transform="translate(120 120)">
        <path d={VX_D} className={onDark ? "fill-white" : "fill-tinta"} />
        <rect x="1611" y="0" width="28" height="710" rx="14" className={onDark ? "fill-white/25" : "fill-borde"} />
        {glow && <path d={AI_D} className="fill-marca" filter={`url(#${id})`} opacity="0.5" />}
        <path d={AI_D} className="fill-marca" />
      </g>
    </svg>
  );
}

// ── Avatar ──────────────────────────────────────────────────────────────────
// WhatsApp Cloud API no entrega foto de perfil: iniciales sobre tinte estable
// derivado del nombre (mismo contacto = mismo color siempre).
const TINTES = [
  "bg-[#e11d1f]/10 text-[#c11315]", "bg-[#0a7ea4]/10 text-[#0a7ea4]",
  "bg-[#7c3aed]/10 text-[#6d28d9]", "bg-[#a16207]/10 text-[#a16207]",
  "bg-[#248a3d]/10 text-[#1a6b2e]", "bg-[#be185d]/10 text-[#be185d]",
];
export function Avatar({ nombre, size = 40 }: { nombre: string; size?: number }) {
  let h = 0;
  for (let i = 0; i < nombre.length; i++) h = (h * 31 + nombre.charCodeAt(i)) >>> 0;
  const tinte = TINTES[h % TINTES.length];
  return (
    <span
      className={`shrink-0 grid place-items-center rounded-full font-semibold ${tinte}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
      aria-hidden="true"
    >
      {iniciales(nombre)}
    </span>
  );
}

// ── Botones ─────────────────────────────────────────────────────────────────
// Feedback al PRESIONAR (no al soltar) y área táctil ≥44px en los tamaños
// táctiles. Un solo primario por pantalla.
const BASE_BTN =
  "inline-flex items-center justify-center gap-2 rounded-lg font-medium whitespace-nowrap shrink-0 " +
  "transition-[transform,background-color,color] duration-100 active:scale-[0.97] " +
  "disabled:opacity-40 disabled:pointer-events-none cursor-pointer select-none";

export function Boton({
  tono = "neutro", tam = "md", className = "", ...p
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  tono?: "marca" | "neutro" | "fantasma" | "peligro"; tam?: "sm" | "md";
}) {
  const tonos = {
    marca: "bg-marca text-white hover:bg-marca-oscuro",
    neutro: "bg-elevado text-tinta border border-borde hover:bg-tenue",
    fantasma: "text-tinta-2 hover:text-tinta hover:bg-tinta/[0.05]",
    peligro: "text-peligro border border-peligro/30 hover:bg-peligro/10",
  }[tono];
  const tams = { sm: "h-8 px-2.5 text-[13px]", md: "h-11 px-4 text-sm md:h-9" }[tam];
  return <button {...p} className={`${BASE_BTN} ${tonos} ${tams} ${className}`} />;
}

// ── Interruptor ─────────────────────────────────────────────────────────────
// Un `<button role="switch">` y no una casilla disfrazada: el lector de
// pantalla tiene que decir "activado/desactivado", no "casilla marcada". La
// etiqueta es parte del control (se puede tocar el texto), que en móvil es la
// diferencia entre acertar y pelear con un objetivo de 20 px.
export function Interruptor({
  activo, onCambiar, etiqueta, detalle, deshabilitado, testId,
}: {
  activo: boolean; onCambiar: (v: boolean) => void; etiqueta: string;
  detalle?: ReactNode; deshabilitado?: boolean;
  /** Marca estable para las varas (T-IDE-8). No se usa para estilos. */
  testId?: string;
}) {
  return (
    <button type="button" role="switch" aria-checked={activo} aria-label={etiqueta}
      disabled={deshabilitado} data-campo={testId}
      onClick={() => onCambiar(!activo)}
      className="w-full flex items-start gap-3 text-left rounded-xl px-3 py-2.5 -mx-1
        hover:bg-tinta/[0.03] disabled:opacity-50 disabled:pointer-events-none
        transition cursor-pointer">
      <span aria-hidden="true"
        className={`mt-0.5 shrink-0 h-6 w-10 rounded-full p-0.5 transition-colors
          ${activo ? "bg-marca" : "bg-borde"}`}>
        <span className={`block h-5 w-5 rounded-full bg-white shadow-sm
          transition-transform duration-200 ${activo ? "translate-x-4" : ""}`} />
      </span>
      <span className="min-w-0">
        <span className="block text-[14px] font-medium">{etiqueta}</span>
        {detalle && <span className="block text-[12.5px] text-tinta-2 leading-snug mt-0.5">{detalle}</span>}
      </span>
    </button>
  );
}

// ── Campo de texto ──────────────────────────────────────────────────────────
// T-IDE-8. Nace aquí y no suelto dentro de `Ajustes.tsx` por lo mismo que
// `Interruptor`: la etiqueta va AMARRADA al control (`htmlFor`/`id` con
// `useId`), que es lo que hace que un lector de pantalla diga qué se está
// editando y que tocar el texto ponga el foco en la caja.
//
// ⚠️⚠️ EL `maxLength` DE AQUÍ ES COMODIDAD, NO DEFENSA, y hay que leerlo así.
// `cargo` y `firma` viajan al system prompt del bot real
// (`worker/src/ia/plantilla_base.ts`), y quien escriba por la API con la sesión
// del dueño NUNCA pasa por este atributo. La baranda de verdad —trim, colapsar
// saltos de línea y recortar al tope— vive en `construirSystem`, que es la
// puerta por la que pasan el worker Y el simulador. Si estos números y los de
// `TOPES_IDENTIDAD` se separan algún día, el motor sigue cortando igual: el peor
// caso es que la pantalla deje escribir de más y el bot lea de menos.
export function Campo({
  etiqueta, valor, onCambiar, deshabilitado, ayuda, maxLength,
  placeholder, requerido, invalido, testId,
}: {
  etiqueta: string; valor: string; onCambiar: (v: string) => void;
  deshabilitado?: boolean; ayuda?: ReactNode; maxLength?: number;
  placeholder?: string; requerido?: boolean; invalido?: boolean; testId?: string;
}) {
  const id = useId();
  return (
    <div className="py-2">
      <label htmlFor={id} className="block text-[14px] font-medium mb-1">
        {etiqueta}
        {!requerido && <span className="text-tinta-2 font-normal"> (opcional)</span>}
      </label>
      <input
        id={id} type="text" value={valor} disabled={deshabilitado}
        maxLength={maxLength} placeholder={placeholder}
        aria-invalid={invalido || undefined}
        data-campo={testId}
        onChange={(e) => onCambiar(e.target.value)}
        className={`w-full h-11 md:h-9 px-3 rounded-lg bg-elevado text-tinta text-sm
          border ${invalido ? "border-peligro" : "border-borde"}
          focus:outline-none focus:ring-2 focus:ring-marca/40
          disabled:opacity-50 disabled:cursor-not-allowed`}
      />
      {ayuda && <p className="text-[12.5px] text-tinta-2 leading-snug mt-1">{ayuda}</p>}
    </div>
  );
}

// ── Lista cerrada ───────────────────────────────────────────────────────────
// `<select>` nativo a propósito: en móvil abre la rueda del sistema, que es
// mucho mejor que cualquier desplegable que dibujemos, y no hay estado que se
// pueda desincronizar. Las opciones llevan etiqueta en español; el VALOR que se
// guarda es el que entiende la base (`formal`/`cercano`/`vendedor`, `es-CO`…),
// que además tiene `check` en la migración 0004.
export function Lista({
  etiqueta, valor, opciones, onCambiar, deshabilitado, ayuda, testId,
}: {
  etiqueta: string; valor: string;
  opciones: ReadonlyArray<{ valor: string; etiqueta: string }>;
  onCambiar: (v: string) => void; deshabilitado?: boolean; ayuda?: ReactNode; testId?: string;
}) {
  const id = useId();
  return (
    <div className="py-2">
      <label htmlFor={id} className="block text-[14px] font-medium mb-1">{etiqueta}</label>
      <select
        id={id} value={valor} disabled={deshabilitado} data-campo={testId}
        onChange={(e) => onCambiar(e.target.value)}
        className="w-full h-11 md:h-9 px-3 rounded-lg bg-elevado text-tinta text-sm
          border border-borde focus:outline-none focus:ring-2 focus:ring-marca/40
          disabled:opacity-50 disabled:cursor-not-allowed">
        {opciones.map((o) => <option key={o.valor} value={o.valor}>{o.etiqueta}</option>)}
      </select>
      {ayuda && <p className="text-[12.5px] text-tinta-2 leading-snug mt-1">{ayuda}</p>}
    </div>
  );
}

// ── Chips / etiquetas ───────────────────────────────────────────────────────
export function Chip({
  children, tono = "neutro", titulo, envolver = false,
}: {
  children: ReactNode; tono?: "neutro" | "marca" | "ok" | "alerta" | "peligro" | "apagado"; titulo?: string;
  /**
   * ⭐ T-CAL-B5 · I-118 · `true` = el texto PARTE en varias líneas en vez de
   * salirse de la pantalla (la píldora del pase en el hilo lleva hasta 19 + 80
   * letras y el celular mide 375 px). Por defecto `false`: todos los demás chips
   * siguen en UNA línea, como siempre. `rounded-xl` y no `rounded-full`: en una
   * línea (~20 px de alto) se ven igual, y en dos o tres no se come las esquinas
   * del texto.
   */
  envolver?: boolean;
}) {
  const tonos = {
    neutro: "bg-tenue text-tinta-2",
    marca: "bg-marca-suave text-acento",
    ok: "bg-ok/10 text-ok",
    alerta: "bg-alerta/12 text-alerta",
    // `peligro` nació para «Esperando asesor» (C7): los otros cinco ya estaban
    // tomados y el chip TIENE que distinguirse del verde de «Asesor» y del
    // ámbar de «24 h vencidas», que va justo al lado.
    // ⚠️ SÓLIDO, y es a propósito: `--color-peligro` y `--color-acento` son EL
    // MISMO hex en los dos temas, así que un tinte al 10 % —como los otros
    // cinco— quedaba con la misma letra que «Bot» y un fondo casi igual, en
    // filas contiguas de la misma lista. Al ser el ÚNICO chip lleno de la
    // pantalla, el que pide acción se ve de un vistazo y no se confunde con
    // ninguno. Si algún día se añade otro chip sólido, esta ventaja se pierde.
    // ⚠️ Y LA LETRA ES `papel`, NO BLANCO. En oscuro `--color-peligro` es un
    // coral CLARO (#f0696b): blanco encima da 2,6:1, por debajo del 4,5:1 que
    // pide AA para 11 px — y este chip existe justamente para LEERSE rápido.
    // `--color-papel` voltea con el tema (#f7f7f8 claro / #131417 oscuro), así
    // que la misma clase da 5,8:1 en claro y 6,1:1 en oscuro sin tocar
    // `--color-peligro`, que la comparten `Boton` y `Aviso`.
    peligro: "bg-peligro text-[var(--color-papel)]",
    apagado: "bg-tenue text-tinta-2 opacity-70",
  }[tono];
  return (
    <span title={titulo}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium ${envolver
        ? "whitespace-normal wrap-anywhere text-center max-w-full rounded-xl"
        : "whitespace-nowrap rounded-full"} ${tonos}`}>
      {children}
    </span>
  );
}

// ── Estados ─────────────────────────────────────────────────────────────────
export function Vacio({ icono, titulo, children, accion }: {
  icono?: ReactNode; titulo: string; children?: ReactNode; accion?: ReactNode;
}) {
  return (
    <div className="h-full grid place-items-center text-center px-6 py-12">
      <div>
        {icono && <div className="opacity-20 mb-3 flex justify-center">{icono}</div>}
        <p className="font-medium text-tinta">{titulo}</p>
        {children && <p className="text-sm text-tinta-2 mt-1 max-w-xs mx-auto">{children}</p>}
        {accion && <div className="mt-4">{accion}</div>}
      </div>
    </div>
  );
}

export function SkeletonLista({ filas = 7 }: { filas?: number }) {
  return (
    <div className="p-3 space-y-3" aria-hidden="true">
      {Array.from({ length: filas }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="skeleton h-10 w-10 rounded-full shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="skeleton h-3.5 rounded" style={{ width: `${45 + (i % 3) * 12}%` }} />
            <div className="skeleton h-3 rounded" style={{ width: `${65 - (i % 4) * 8}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Aviso corto no bloqueante (errores del envío, ventana cerrada…). */
export function Aviso({ tono = "alerta", children, onCerrar }: {
  tono?: "alerta" | "peligro" | "ok"; children: ReactNode; onCerrar?: () => void;
}) {
  const tonos = {
    alerta: "bg-alerta/10 text-alerta border-alerta/25",
    peligro: "bg-peligro/10 text-peligro border-peligro/25",
    ok: "bg-ok/10 text-ok border-ok/25",
  }[tono];
  return (
    <div role="status" aria-live="polite"
      className={`entra flex items-start gap-2 text-[13px] leading-snug border rounded-lg px-3 py-2 ${tonos}`}>
      <span className="flex-1">{children}</span>
      {onCerrar && (
        <button onClick={onCerrar} aria-label="Cerrar aviso"
          className="opacity-60 hover:opacity-100 cursor-pointer">✕</button>
      )}
    </div>
  );
}
